/**
 * Teacher Dashboard
 * Main dashboard for teachers to manage courses and lectures
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/layout/Layout';
import Mascot from '../../components/shared/Mascot';
import CourseCardPlayful from '../../components/courses/CourseCardPlayful';
import Modal from '../../components/shared/Modal';
import Input from '../../components/shared/Input';
import Button from '../../components/shared/Button';
import UpNextCard from '../../components/shared/UpNextCard';
import { startReminderChecker } from '../../utils/reminders';
import {
  getCourses,
  createCourse,
  deleteCourse,
  uploadLecture,
  CourseResponse,
  getUpcomingQuizzes,
  UpcomingQuizItem,
} from '../../api/courses';
import {
  listPDPRConversations,
  createPDPRConversation,
  getPDPRConversation,
  deletePDPRConversation,
  sendPDPRMessage,
  PDPRConversation,
  PDPRMessage,
} from '../../api/pdpr';

type DashboardTab = 'courses' | 'pedagogical';

const DASHBOARD_TABS: { id: DashboardTab; label: string; icon: React.ReactNode }[] = [
  {
    id: 'courses',
    label: 'My Courses',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
  },
  {
    id: 'pedagogical',
    label: 'Pedagogical Practices',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
  },
];

const TeacherDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<DashboardTab>('courses');
  const [courses, setCourses] = useState<CourseResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [semesterFilter, setSemesterFilter] = useState<string>('all');

  // Create Course Modal
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const currentYear = new Date().getFullYear();
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    semester: '',
    year: currentYear,
  });
  const [courseErrors, setCourseErrors] = useState<{ title?: string; general?: string }>({});

  // Upload Lecture Modal
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedCourseForUpload, setSelectedCourseForUpload] = useState<string>('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [lectureForm, setLectureForm] = useState({
    title: '',
    description: '',
    videoFile: null as File | null,
  });
  const [lectureErrors, setLectureErrors] = useState<{ title?: string; video?: string; general?: string }>({});

  // Delete confirmation
  const [courseToDelete, setCourseToDelete] = useState<string | null>(null);

  // Up next: upcoming published quizzes across the teacher's courses
  const [upcomingQuizzes, setUpcomingQuizzes] = useState<UpcomingQuizItem[]>([]);
  const upcomingQuizzesRef = useRef<UpcomingQuizItem[]>([]);

  // PDPR Chat state
  const [conversations, setConversations] = useState<PDPRConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<PDPRMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load courses
  useEffect(() => {
    loadCourses();
  }, [user]);

  // Load upcoming quizzes for the "Up next" section
  useEffect(() => {
    getUpcomingQuizzes()
      .then(setUpcomingQuizzes)
      .catch(() => {});
  }, []);

  // Keep the ref fresh so the reminder checker always sees current items
  useEffect(() => {
    upcomingQuizzesRef.current = upcomingQuizzes;
  }, [upcomingQuizzes]);

  // Periodic reminder checker (browser notifications for opted-in quizzes)
  useEffect(() => startReminderChecker(() => upcomingQuizzesRef.current), []);

  // Load PDPR conversations when tab switches
  useEffect(() => {
    if (activeTab === 'pedagogical') {
      loadConversations();
    }
  }, [activeTab]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getCourses({ instructor: user?.username });
      setCourses(data);
    } catch (err: any) {
      setError('Failed to load courses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Create course handlers
  const handleCreateCourse = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    if (!courseForm.title.trim()) {
      setCourseErrors({ title: 'Course title is required' });
      return;
    }

    setCreateLoading(true);
    setCourseErrors({});

    try {
      await createCourse({
        title: courseForm.title,
        description: courseForm.description,
        semester: courseForm.semester,
        year: courseForm.year,
      });

      // Reset form and close modal
      setCourseForm({ title: '', description: '', semester: '', year: currentYear });
      setIsCreateModalOpen(false);

      // Reload courses
      await loadCourses();
    } catch (err: any) {
      setCourseErrors({
        general: err.response?.data?.detail || 'Failed to create course',
      });
    } finally {
      setCreateLoading(false);
    }
  };

  // Upload lecture handlers
  const handleOpenUploadModal = (courseId: string) => {
    setSelectedCourseForUpload(courseId);
    setIsUploadModalOpen(true);
  };

  const handleVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('video/')) {
        setLectureErrors({ video: 'Please select a valid video file' });
        return;
      }

      setLectureForm({ ...lectureForm, videoFile: file });
      setLectureErrors({ ...lectureErrors, video: undefined });
    }
  };

  const handleUploadLecture = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    const errors: typeof lectureErrors = {};
    if (!lectureForm.title.trim()) {
      errors.title = 'Lecture title is required';
    }
    if (!lectureForm.videoFile) {
      errors.video = 'Please select a video file';
    }

    if (Object.keys(errors).length > 0) {
      setLectureErrors(errors);
      return;
    }

    setUploadLoading(true);
    setLectureErrors({});
    setUploadProgress(0);

    try {
      await uploadLecture(
        selectedCourseForUpload,
        lectureForm.title,
        lectureForm.description,
        lectureForm.videoFile!,
        (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      );

      // Reset form and close modal
      setLectureForm({ title: '', description: '', videoFile: null });
      setIsUploadModalOpen(false);
      setUploadProgress(0);

      // Reload courses
      await loadCourses();
    } catch (err: any) {
      setLectureErrors({
        general: err.response?.data?.detail || 'Failed to upload lecture',
      });
    } finally {
      setUploadLoading(false);
    }
  };

  // Delete course
  const handleDeleteCourse = async (courseId: string) => {
    try {
      await deleteCourse(courseId);
      setCourseToDelete(null);
      await loadCourses();
    } catch (err: any) {
      alert('Failed to delete course');
      console.error(err);
    }
  };

  // PDPR Chat handlers
  const loadConversations = async () => {
    try {
      setConversationsLoading(true);
      const data = await listPDPRConversations();
      setConversations(data);
    } catch (err) {
      console.error('Failed to load PDPR conversations:', err);
    } finally {
      setConversationsLoading(false);
    }
  };

  const handleNewConversation = async () => {
    try {
      const conv = await createPDPRConversation();
      setConversations(prev => [conv, ...prev]);
      setActiveConversationId(conv.conversation_id);
      setMessages([]);
    } catch (err) {
      console.error('Failed to create conversation:', err);
    }
  };

  const handleSelectConversation = async (conversationId: string) => {
    try {
      setActiveConversationId(conversationId);
      setChatLoading(true);
      const data = await getPDPRConversation(conversationId);
      setMessages(data.messages);
    } catch (err) {
      console.error('Failed to load conversation:', err);
    } finally {
      setChatLoading(false);
    }
  };

  const handleDeleteConversation = async (conversationId: string) => {
    try {
      await deletePDPRConversation(conversationId);
      setConversations(prev => prev.filter(c => c.conversation_id !== conversationId));
      if (activeConversationId === conversationId) {
        setActiveConversationId(null);
        setMessages([]);
      }
      setConversationToDelete(null);
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim() || !activeConversationId || chatLoading) return;

    const userContent = chatInput.trim();
    setChatInput('');
    setChatLoading(true);

    // Optimistically add user message
    const tempUserMsg: PDPRMessage = {
      message_id: `temp_${Date.now()}`,
      conversation_id: activeConversationId,
      role: 'user',
      content: userContent,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const result = await sendPDPRMessage(activeConversationId, userContent);
      // Replace temp user message with real one, add assistant message
      setMessages(prev => [
        ...prev.filter(m => m.message_id !== tempUserMsg.message_id),
        result.user_message,
        result.assistant_message,
      ]);
      // Update conversation title in sidebar
      setConversations(prev => prev.map(c =>
        c.conversation_id === activeConversationId
          ? { ...c, title: userContent.slice(0, 60) + (userContent.length > 60 ? '...' : ''), updated_at: new Date().toISOString() }
          : c
      ));
    } catch (err: any) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.message_id !== tempUserMsg.message_id));
      alert(err.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setChatLoading(false);
    }
  };

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Semester filtering — terms derived from the loaded courses, newest first
  const SEMESTER_ORDER: Record<string, number> = { Spring: 0, Summer: 1, Fall: 2 };
  const termOf = (c: CourseResponse) => (c.semester && c.year ? `${c.semester} ${c.year}` : null);
  const terms = Array.from(new Set(courses.map(termOf).filter((t): t is string => t !== null))).sort((a, b) => {
    const [sa, ya] = a.split(' ');
    const [sb, yb] = b.split(' ');
    return Number(yb) - Number(ya) || (SEMESTER_ORDER[sb] ?? 0) - (SEMESTER_ORDER[sa] ?? 0);
  });
  const filteredCourses =
    semesterFilter === 'all'
      ? courses
      : semesterFilter === 'none'
        ? courses.filter((c) => !termOf(c))
        : courses.filter((c) => termOf(c) === semesterFilter);
  const hasUntermed = courses.some((c) => !termOf(c));

  // Simple markdown-like rendering for assistant messages
  const renderMarkdown = (text: string) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^### (.*$)/gm, '<h4 class="text-base font-semibold mt-3 mb-1">$1</h4>')
      .replace(/^## (.*$)/gm, '<h3 class="text-lg font-semibold mt-4 mb-1">$1</h3>')
      .replace(/^# (.*$)/gm, '<h2 class="text-xl font-bold mt-4 mb-2">$1</h2>')
      .replace(/^- (.*$)/gm, '<li class="ml-4 list-disc">$1</li>')
      .replace(/^(\d+)\. (.*$)/gm, '<li class="ml-4 list-decimal">$1. $2</li>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <Layout>
      <div className="space-y-8">
        {/* === Header === */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
              Welcome back, {user?.full_name || user?.username || 'there'}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            Create course
          </button>
        </div>

        {/* === Up next: upcoming quiz deadlines === */}
        {upcomingQuizzes.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">Up next</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {upcomingQuizzes.slice(0, 3).map((item) => (
                <UpNextCard
                  key={item.quiz_id}
                  item={item}
                  onOpen={() => navigate(`/teacher/courses/${item.course_id}`)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Dashboard Tabs — segmented pills */}
        <div className="inline-flex rounded-full bg-gray-100 p-1 dark:bg-secondary-800">
          {DASHBOARD_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-primary-700 shadow-sm dark:bg-secondary-700 dark:text-white'
                  : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.icon}
              {tab.label}
              {tab.id === 'courses' && courses.length > 0 && (
                <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold ${
                  activeTab === 'courses'
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                    : 'bg-gray-200 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                }`}>
                  {courses.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="animate-fade-in" key={activeTab}>
          {/* === MY COURSES TAB === */}
          {activeTab === 'courses' && (
            <div className="space-y-6">
              {/* Semester filter */}
              {!loading && courses.length > 0 && terms.length > 0 && (
                <div className="flex items-center justify-end gap-2">
                  <label htmlFor="semester-filter" className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                    Semester
                  </label>
                  <select
                    id="semester-filter"
                    value={semesterFilter}
                    onChange={(e) => setSemesterFilter(e.target.value)}
                    className="rounded-full border-2 border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-800 dark:text-gray-200"
                  >
                    <option value="all">All semesters</option>
                    {terms.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                    {hasUntermed && <option value="none">No semester set</option>}
                  </select>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-3 rounded-2xl border-l-4 border-red-500 bg-red-50 px-4 py-3 text-red-700 dark:bg-red-900/20 dark:text-red-400">
                  <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
                  </svg>
                  {error}
                </div>
              )}

              {/* Loading */}
              {loading && (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
              )}

              {/* Empty State */}
              {!loading && courses.length === 0 && (
                <div className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-gray-200 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                  <Mascot name="folder" chip="#93c5fd" className="h-16 w-16 shadow-medium animate-float-slow" blinkDelay={0.5} />
                  <h3 className="mt-6 text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">No courses yet</h3>
                  <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">
                    Create your first course, upload a lecture, and let the AI tutor take it from there.
                  </p>
                  <div className="mt-6">
                    <Button onClick={() => setIsCreateModalOpen(true)}>Create your first course</Button>
                  </div>
                </div>
              )}

              {/* Courses Grid */}
              {!loading && courses.length > 0 && filteredCourses.length > 0 && (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
                  {filteredCourses.map((course) => (
                    <CourseCardPlayful
                      key={course.course_id}
                      course={course}
                      onClick={() => navigate(`/teacher/courses/${course.course_id}`)}
                      ctaLabel="Manage"
                      showActions
                      onDelete={() => setCourseToDelete(course.course_id)}
                    />
                  ))}
                </div>
              )}

              {/* No courses match the filter */}
              {!loading && courses.length > 0 && filteredCourses.length === 0 && (
                <div className="rounded-2xl border border-gray-200 bg-white px-6 py-10 text-center dark:border-secondary-700 dark:bg-secondary-800">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    No courses in {semesterFilter === 'none' ? 'this view' : semesterFilter}.
                  </p>
                  <button
                    onClick={() => setSemesterFilter('all')}
                    className="mt-3 text-sm font-semibold text-primary-700 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200"
                  >
                    Show all semesters
                  </button>
                </div>
              )}
            </div>
          )}

          {/* === PEDAGOGICAL PRACTICES TAB === */}
          {activeTab === 'pedagogical' && (
            <div className="flex gap-4" style={{ height: 'calc(100vh - 260px)', minHeight: '500px' }}>
              {/* Conversation Sidebar */}
              <div className="w-72 flex-shrink-0 bg-white dark:bg-secondary-800 rounded-3xl border border-gray-200 dark:border-secondary-700 shadow-soft flex flex-col overflow-hidden">
                {/* Sidebar Header */}
                <div className="p-4 border-b border-gray-200 dark:border-secondary-700">
                  <button
                    onClick={handleNewConversation}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-primary-600 px-4 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    </svg>
                    New conversation
                  </button>
                </div>

                {/* Conversation List */}
                <div className="flex-1 overflow-y-auto">
                  {conversationsLoading && (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                    </div>
                  )}
                  {!conversationsLoading && conversations.length === 0 && (
                    <div className="px-4 py-8 text-center">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">No conversations yet</p>
                    </div>
                  )}
                  {conversations.map((conv) => (
                    <div
                      key={conv.conversation_id}
                      className={`group flex items-center gap-2 px-4 py-3 cursor-pointer border-b border-gray-100 dark:border-secondary-700 transition-colors duration-150 ${
                        activeConversationId === conv.conversation_id
                          ? 'bg-primary-50 dark:bg-primary-900/20 border-l-2 border-l-primary-600'
                          : 'hover:bg-gray-50 dark:hover:bg-secondary-750 border-l-2 border-l-transparent'
                      }`}
                      onClick={() => handleSelectConversation(conv.conversation_id)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-bold truncate ${
                          activeConversationId === conv.conversation_id
                            ? 'text-primary-700 dark:text-primary-400'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}>
                          {conv.title}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {new Date(conv.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setConversationToDelete(conv.conversation_id); }}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all duration-150"
                        title="Delete conversation"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Chat Area */}
              <div className="flex-1 bg-white dark:bg-secondary-800 rounded-3xl border border-gray-200 dark:border-secondary-700 shadow-soft flex flex-col overflow-hidden">
                {!activeConversationId ? (
                  /* Empty state — no conversation selected */
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center max-w-md px-6">
                      <div className="mb-5 flex justify-center">
                        <Mascot name="bulb" chip="#fb923c" className="h-16 w-16 shadow-medium animate-float-slow" blinkDelay={0.4} />
                      </div>
                      <h3 className="text-lg font-extrabold tracking-tight text-gray-900 dark:text-white mb-2">Pedagogical Practices Assistant</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                        Ask about teaching challenges and get research-backed solutions from published educational research.
                      </p>
                      <div className="space-y-2 text-left">
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Try asking about:</p>
                        {[
                          'How can I improve student engagement in large lectures?',
                          'What are effective strategies for formative assessment?',
                          'How do I implement a flipped classroom model?',
                        ].map((suggestion, i) => (
                          <button
                            key={i}
                            onClick={async () => {
                              const conv = await createPDPRConversation();
                              setConversations(prev => [conv, ...prev]);
                              setActiveConversationId(conv.conversation_id);
                              setMessages([]);
                              setChatInput(suggestion);
                            }}
                            className="w-full rounded-full border-2 border-gray-200 px-4 py-2.5 text-left text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 dark:border-secondary-700 dark:text-gray-300 dark:hover:bg-secondary-700"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                      {messages.length === 0 && !chatLoading && (
                        <div className="text-center py-12 text-gray-400 dark:text-gray-500">
                          <p className="text-sm">Start by describing a teaching challenge you're facing.</p>
                        </div>
                      )}
                      {messages.map((msg) => (
                        <div
                          key={msg.message_id}
                          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                            msg.role === 'user'
                              ? 'bg-primary-600 text-gray-900 rounded-br-md'
                              : 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-gray-200 rounded-bl-md'
                          }`}>
                            {msg.role === 'assistant' ? (
                              <div
                                className="text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none"
                                dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                              />
                            ) : (
                              <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                            )}
                          </div>
                        </div>
                      ))}
                      {chatLoading && messages.length > 0 && messages[messages.length - 1]?.role === 'user' && (
                        <div className="flex justify-start">
                          <div className="bg-gray-100 dark:bg-secondary-700 rounded-2xl rounded-bl-md px-4 py-3">
                            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                              <div className="flex gap-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                              </div>
                              Researching pedagogical practices...
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="border-t border-gray-200 dark:border-secondary-700 p-4">
                      <form onSubmit={handleSendMessage} className="flex gap-3">
                        <input
                          type="text"
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          placeholder="Describe a teaching challenge or ask about pedagogical strategies..."
                          disabled={chatLoading}
                          className="flex-1 rounded-full border-2 border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 transition-colors duration-200 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:opacity-50 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white dark:placeholder-secondary-400"
                        />
                        <button
                          type="submit"
                          disabled={chatLoading || !chatInput.trim()}
                          className="flex items-center gap-2 rounded-full bg-primary-600 px-4 py-2.5 font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:bg-gray-300 disabled:shadow-none disabled:dark:bg-secondary-600"
                        >
                          {chatLoading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                          )}
                        </button>
                      </form>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Course Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create New Course"
        size="md"
      >
        <form onSubmit={handleCreateCourse} className="space-y-5">
          {courseErrors.general && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-red-700 dark:text-red-400 px-4 py-3 rounded-2xl text-sm">
              {courseErrors.general}
            </div>
          )}

          <Input
            label="Course Title"
            placeholder="e.g., Introduction to Python Programming"
            value={courseForm.title}
            onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
            error={courseErrors.title}
            fullWidth
            required
          />

          <div>
            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
              Description
            </label>
            <textarea
              placeholder="Describe what students will learn in this course..."
              value={courseForm.description}
              onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
              className="block w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors duration-200"
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                Semester <span className="text-red-500">*</span>
              </label>
              <select
                value={courseForm.semester}
                onChange={(e) => setCourseForm({ ...courseForm, semester: e.target.value })}
                className="block w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors duration-200"
                required
              >
                <option value="">Select semester</option>
                <option value="Spring">Spring</option>
                <option value="Summer">Summer</option>
                <option value="Fall">Fall</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                Year <span className="text-red-500">*</span>
              </label>
              <select
                value={courseForm.year}
                onChange={(e) => setCourseForm({ ...courseForm, year: parseInt(e.target.value) })}
                className="block w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors duration-200"
                required
              >
                {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsCreateModalOpen(false)}
              fullWidth
            >
              Cancel
            </Button>
            <Button type="submit" loading={createLoading} fullWidth>
              Create Course
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Conversation Confirmation */}
      {conversationToDelete && (
        <Modal
          isOpen={!!conversationToDelete}
          onClose={() => setConversationToDelete(null)}
          title="Delete Conversation"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              Are you sure you want to delete this conversation? This action cannot be undone.
            </p>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setConversationToDelete(null)} fullWidth>
                Cancel
              </Button>
              <Button type="button" variant="danger" onClick={() => handleDeleteConversation(conversationToDelete)} fullWidth>
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Course Confirmation Modal */}
      {courseToDelete && (
        <Modal
          isOpen={!!courseToDelete}
          onClose={() => setCourseToDelete(null)}
          title="Delete Course"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              Are you sure you want to delete this course? This action cannot be undone and will
              delete all associated lectures.
            </p>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setCourseToDelete(null)}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={() => handleDeleteCourse(courseToDelete)}
                fullWidth
              >
                Delete Course
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default TeacherDashboard;
